import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import SettingsContext from "../../contexts/SettingsProvider";
import { ITrip } from "../../interfaces/common-interfaces";
import { dateUtil } from "../../utility/dateUtil";
import { useDockDisplayService } from "./useDockDisplayService";
import { localise } from "../../utility/localizationUtil";
import MarqueeDisplay from "./MarqueeDisplay";
import TimeStatusBadge from "../../components/badges/TimeStatusBadge";

const DockDisplay: React.FC = () => {
  const { settings } = useContext(SettingsContext);
  const { fetchTrips } = useDockDisplayService();

  const [trip, setTrip] = useState<ITrip>();
  const [nextVesselTime, setNextVesselTime] = useState("");

  const [isAssamese, setIsAssamese] = useState(false);

  const [currentTime, setCurrentTime] = useState("");

  // Toggle language
  useEffect(() => {
    const intervalSec = settings.languageToggleInterval || 10;
    const intervalMs = intervalSec * 1000;

    if (intervalMs < 1000) {
      console.warn(
        "Language toggle interval too low, defaulting to 10 seconds"
      );
    }

    const langInterval = setInterval(() => {
      setIsAssamese((prev) => !prev);
    }, intervalMs);

    return () => clearInterval(langInterval);
  }, []);

  // Update time every second in IST
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeString = now
        .toLocaleTimeString("en-GB", {
          timeZone: "Asia/Kolkata",
          hour12: false,
        }) // IST in 24-hour format
        .padStart(8, "0"); // Ensure leading zeroes
      setCurrentTime(timeString);
    };

    const timer = setInterval(updateClock, 1000);
    updateClock(); // Set the time immediately

    return () => clearInterval(timer); // Cleanup interval
  }, []);

  // API Call to fetch trips
  useEffect(() => {
    if (settings.ghat !== "") {
      fetchTrips(setTrip, setNextVesselTime, settings.ghat); // Initial fetch

      const intervalSec = settings.apiCallFrequency || 10;
      const intervalMs = intervalSec * 1000;

      if (intervalMs < 1000) {
        console.warn("API call frequency too low, defaulting to 10 seconds");
      }

      const fetchInterval = setInterval(() => {
        fetchTrips(setTrip, setNextVesselTime, settings.ghat);
      }, intervalMs);

      return () => clearInterval(fetchInterval); // Cleanup on unmount or ghat change
    } else {
      // toast.error("Please select a ghat in the settings");
      console.log("Please select a ghat in the settings");
    }
  }, [settings.ghat]);

  if (!trip) {
    return (
      <div className="bg-slate-800 text-white min-h-screen w-screen overflow-x-hidden flex justify-center pt-20">
        <div className="px-20">
          <div className="text-center mb-10">
            <div
              className={`font-semibold mb-2`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {localise(isAssamese ? "as" : "en", settings.ghatName)}
            </div>
            <div
              className={`font-semibold`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {dateUtil.formatDateWithDay(new Date().toISOString())}
            </div>
          </div>
          <div className=" text-[#FFD233] mt-20 mb-20">
            <div
              className={`flex items-center justify-center text-center gap-x-4 font-semibold mb-2`}
              style={{ fontSize: `${settings.header1FontSize}px` }}
            >
              {isAssamese
                ? `এই বার্থত বৰ্তমান কনো যাত্ৰাৰ সময়সূচী নাই`
                : `No trip scheduled from this berth at the moment`}
            </div>
          </div>
          <div className="pt-3 pb-2 text-center">
            <h3
              className={`font-bold text-yellow-400`}
              style={{ fontSize: `${settings.header1FontSize}px` }}
            >
              {isAssamese ? "বাৰ্থ" : "Berth"} {settings.dockNo}
            </h3>
          </div>
          <div className="w-screen overflow-hidden">
            <div className="mt-12 px-12">
              {settings.displayTextList && settings.displayTextList.length > 0 && (
                <MarqueeDisplay
                  fontSize={Number(settings.header2FontSize)}
                  messages={settings.displayTextList}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 text-white min-h-screen">
      <div className="px-20 py-10">
        <div className="flex justify-between items-center text-[#FFD233] mb-8">
          <div>
            <div
              className={`flex items-center gap-x-4 font-semibold mb-2`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {localise(
                isAssamese ? "as" : "en",
                trip?.route.ghatFrom.ghatName
              )}
            </div>
            <div
              className={`font-semibold`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {dateUtil.formatDateWithDay(new Date().toISOString())}
            </div>
          </div>

          <div
            className={`flex flex-col items-end gap-y-2`}
            style={{ fontSize: `${settings.tableBodyFontSize}px` }}
          >
            <div>
              <span className="inline-block bg-green-500 text-black text-lg font-semibold py-1 px-3 rounded-full">
                {trip?.timeStatus}
              </span>
            </div>
            <div className="font-semibold flex gap-4 items-center">
              <AccessTimeFilledIcon />
              <span>{currentTime}</span>
            </div>
          </div>
        </div>

        <hr className="mb-6" />

        <div className="text-center space-y-6 mt-12">
          <div>
            <h3
              className={`uppercase text-yellow-400`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {isAssamese ? "গন্তব্যস্থান" : "Destination"}
            </h3>
            <p
              className={`font-bold text-white mt-1`}
              style={{ fontSize: `${settings.header1FontSize}px` }}
            >
              {localise(isAssamese ? "as" : "en", trip?.route.ghatTo.ghatName)}
            </p>
          </div>

          <div>
            <h3
              className={`uppercase text-gray-400`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {isAssamese ? "প্ৰস্থানৰ সময়" : "Departure"}
            </h3>
            <p
              className={`text-white font-bold mt-1`}
              style={{ fontSize: `${settings.header1FontSize}px` }}
            >
              {trip && dateUtil.formatTime(trip.departureTime)}
            </p>
          </div>

          <div className="pt-3 pb-2">
            <h3
              className={`font-bold text-yellow-400`}
              style={{ fontSize: `${settings.header1FontSize}px` }}
            >
              {isAssamese ? "বাৰ্থ" : "Berth"} {trip?.dockNo}
            </h3>
          </div>

          <div>
            <h3
              className={`uppercase text-gray-400`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {isAssamese ? "জাহাজ" : "Vessel"}
            </h3>
            <p
              className={`text-white mt-1`}
              style={{ fontSize: `${settings.header2FontSize}px` }}
            >
              {localise(isAssamese ? "as" : "en", trip?.vessel.name)}
            </p>
          </div>

          <div>
            <h3
              className={`uppercase text-gray-400`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {isAssamese ? "সময়কাল" : "Duration"}
            </h3>
            <p
              className={`text-white mt-1`}
              style={{ fontSize: `${settings.header2FontSize}px` }}
            >
              {trip && dateUtil.formatDuration(trip.tripDuration)}
            </p>
          </div>

          <div>
            <h3
              className={`uppercase text-gray-400`}
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}
            >
              {isAssamese ? "স্থিতি" : "Status"}
            </h3>
            <div
              className="my-8 flex justify-center font-bold"
              style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
              <TimeStatusBadge timeStatus={trip.timeStatus} />
            </div>
          </div>

          <hr />

          {nextVesselTime !== "" && (
            <div>
              <p
                className={`text-gray-400`}
                style={{ fontSize: `${settings.tableBodyFontSize}px` }}
              >
                {isAssamese
                  ? `পৰৱৰ্তী জাহাজৰ সময়: ${dateUtil.formatTime(nextVesselTime)}`
                  : `Next Vessel at: ${dateUtil.formatTime(nextVesselTime)}`}
              </p>
            </div>
          )}
        </div>

        <div>
          {settings.displayTextList && settings.displayTextList.length > 0 && (
            <MarqueeDisplay
              fontSize={Number(settings.header2FontSize)}
              messages={settings.displayTextList}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DockDisplay;
